import axios, { AxiosInstance, AxiosResponse } from 'axios'

// Store reference will be set after initialization to avoid circular dependency
let storeRef: any = null
let clearTokensAction: any = null
let setTokensAction: any = null

// Function to set store reference (called after store creation)
export const setApiStoreRef = (store: any, clearTokens: any, setTokens: any) => {
    storeRef = store
    clearTokensAction = clearTokens
    setTokensAction = setTokens
}

// Create axios instance
const api: AxiosInstance = axios.create({
    baseURL: (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api/v1',
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json',
    },
})

// Provide a non-hook fallback snackbar utility for non-React contexts
let snackbar: { error: (msg: string) => void } | null = null
export const setGlobalSnackbar = (handlers: { error: (msg: string) => void }) => {
    snackbar = handlers
}

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        if (storeRef) {
            const state = storeRef.getState()
            const token = state.auth.token

            if (token) {
                config.headers.Authorization = `Bearer ${token}`
            }
        }

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor to handle token refresh and errors
api.interceptors.response.use(
    (response: AxiosResponse) => {
        return response
    },
    async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry && storeRef && clearTokensAction && setTokensAction) {
            originalRequest._retry = true

            const state = storeRef.getState()
            const refreshToken = state.auth.refreshToken

            if (refreshToken) {
                try {
                    const response = await axios.post(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/v1/auth/refresh-token`, {
                        refreshToken,
                    })

                    const { token, refreshToken: newRefreshToken } = response.data

                    storeRef.dispatch(setTokensAction({ token, refreshToken: newRefreshToken }))

                    // Retry original request with new token
                    originalRequest.headers.Authorization = `Bearer ${token}`
                    return api(originalRequest)
                } catch (refreshError) {
                    // Refresh failed, clear tokens and redirect to login
                    storeRef.dispatch(clearTokensAction())
                    window.location.href = '/login'
                    return Promise.reject(refreshError)
                }
            } else {
                // No refresh token, clear auth and redirect
                storeRef.dispatch(clearTokensAction())
                window.location.href = '/login'
            }
        }

        // Handle other error types with global snackbar if available
        const msg = error.response?.status >= 500
            ? 'Server error. Please try again later.'
            : error.code === 'NETWORK_ERROR'
                ? 'Network error. Please check your connection.'
                : error.code === 'ECONNABORTED'
                    ? 'Request timeout. Please try again.'
                    : ''

        if (msg && snackbar) {
            snackbar.error(msg)
        }

        return Promise.reject(error)
    }
)

// API response interface
export interface ApiResponse<T = any> {
    data: T
    message?: string
    success: boolean
}

// API error interface
export interface ApiError {
    message: string
    statusCode: number
    errors?: Record<string, string[]>
}

// Export the configured axios instance
export default api

// Re-export specific API modules
export * from './auth'
export * from './gigs'
export * from './applications'
export * from './messages'
export * from './notifications'
export * from './users'
export * from './upload'
export * from './profile'
export * from './gigCategories'
export * from './currency'
export * from './successStories'
export * from './blog'
export * from './faqs'
export * from './subscriptions'
export * from './wallet'